import { renderTerm, renderTermExplicit, COLORS } from './prologRenderer';

// Renders a token array as coloured inline spans.
export function Tokens({ tokens }) {
    return <>{tokens.map((t, i) =>
        t.sub
            ? <sub key={i} style={{ color: t.color, fontSize: '0.75em' }}>{t.text}</sub>
            : <span key={i} style={{ color: t.color }}>{t.text}</span>
    )}</>;
}

// Renders a single term as coloured inline spans.
export function TermSpan({ idx, termVec, idVec, varIdVec }) {
    if (!termVec || !idVec || !varIdVec || idx < 0) return null;
    return <Tokens tokens={renderTerm(idx, termVec, idVec, varIdVec)} />;
}

// Renders a bindings line: X = val, Y = val2, ...
export function BindingsLine({ bindings }) {
    if (!bindings.length) return null;
    return (
        <div className="frame-bindings">
            {bindings.map(({ nameTokens, valueTokens }, i) => (
                <span key={i}>
                    {i > 0 && <span style={{ color: COLORS.comma }}>, </span>}
                    <Tokens tokens={nameTokens} />
                    <span style={{ color: COLORS.operator }}> = </span>
                    <Tokens tokens={valueTokens} />
                </span>
            ))}
        </div>
    );
}

// Renders the content of a frame, dimming inactive children for conjunctions/disjunctions.
// When showBindings is true, bound variables are shown as unbound with a bindings line below.
export function FrameContent({ frame, termVec, idVec, varIdVec, isActive, showBindings }) {
    if (showBindings) {
        const sharedBindings = new Map();

        let mainContent;
        if (frame.type === 'CONJUNCTION') {
            const term = termVec[frame.index];
            if (!term) {
                const { tokens } = renderTermExplicit(frame.index, termVec, idVec, varIdVec, sharedBindings);
                mainContent = <Tokens tokens={tokens} />;
            } else {
                const activeIdx = isActive ? frame.continuation.next : frame.continuation.next - 1;
                mainContent = term.children.map((childIdx, i) => {
                    const { tokens } = renderTermExplicit(childIdx, termVec, idVec, varIdVec, sharedBindings);
                    return (
                        <span key={i} style={{ opacity: i === activeIdx ? 1 : 0.3 }}>
                            {i > 0 && <span style={{ color: COLORS.comma }}>, </span>}
                            <Tokens tokens={tokens} />
                        </span>
                    );
                });
            }
        } else if (frame.type === 'DISJUNCTION') {
            const term = termVec[frame.index];
            if (!term) {
                const { tokens } = renderTermExplicit(frame.index, termVec, idVec, varIdVec, sharedBindings);
                mainContent = <Tokens tokens={tokens} />;
            } else {
                const activeIdx = frame.decisionIndex;
                mainContent = term.children.map((childIdx, i) => {
                    const { tokens } = renderTermExplicit(childIdx, termVec, idVec, varIdVec, sharedBindings);
                    return (
                        <span key={i} style={{ opacity: i === activeIdx ? 1 : 0.3 }}>
                            {i > 0 && <span style={{ color: COLORS.semicolon }}> ; </span>}
                            <Tokens tokens={tokens} />
                        </span>
                    );
                });
            }
        } else {
            const { tokens } = renderTermExplicit(frame.index, termVec, idVec, varIdVec, sharedBindings);
            mainContent = <Tokens tokens={tokens} />;
        }

        return (
            <>
                <div>{mainContent}</div>
                <BindingsLine bindings={[...sharedBindings.values()]} />
            </>
        );
    }

    // Inline mode (existing behaviour):
    if (frame.type === 'CONJUNCTION') {
        const term = termVec[frame.index];
        if (!term) return <TermSpan idx={frame.index} termVec={termVec} idVec={idVec} varIdVec={varIdVec} />;
        const activeIdx = isActive ? frame.continuation.next : frame.continuation.next - 1;
        return term.children.map((childIdx, i) => (
            <span key={i} style={{ opacity: i === activeIdx ? 1 : 0.3 }}>
                {i > 0 && <span style={{ color: COLORS.comma }}>, </span>}
                <TermSpan idx={childIdx} termVec={termVec} idVec={idVec} varIdVec={varIdVec} />
            </span>
        ));
    }
    if (frame.type === 'DISJUNCTION') {
        const term = termVec[frame.index];
        if (!term) return <TermSpan idx={frame.index} termVec={termVec} idVec={idVec} varIdVec={varIdVec} />;
        const activeIdx = frame.decisionIndex;
        return term.children.map((childIdx, i) => (
            <span key={i} style={{ opacity: i === activeIdx ? 1 : 0.3 }}>
                {i > 0 && <span style={{ color: COLORS.semicolon }}> ; </span>}
                <TermSpan idx={childIdx} termVec={termVec} idVec={idVec} varIdVec={varIdVec} />
            </span>
        ));
    }
    return <TermSpan idx={frame.index} termVec={termVec} idVec={idVec} varIdVec={varIdVec} />;
}

// Renders the variable bindings for a found solution.
export function SolutionDisplay({ termVec, namedVars, idVec, varIdVec }) {
    if (!namedVars.length)
        return <pre className="solution-box">true.</pre>;
    return (
        <pre className="solution-box">
            {namedVars.map(({ name, termIdx }, i) => (
                <span key={name}>
                    {i > 0 ? '\n' : ''}
                    <span style={{ color: COLORS.variable }}>{name}</span>
                    <span style={{ color: COLORS.operator }}> = </span>
                    <TermSpan idx={termIdx} termVec={termVec} idVec={idVec} varIdVec={varIdVec} />
                </span>
            ))}
        </pre>
    );
}
